import React, { useState } from 'react'
import styled from 'styled-components'
import { Tooltip, IconButton, Dialog } from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useSelector } from 'react-redux'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import APIService from 'services/APIService'
import { toast } from 'react-toastify'
import { getApiErrors } from 'utils/errors'
import UserAvatar from 'components/Avatar'
import { buildingRoleLabel } from 'utils/roles'
import { hideEmail } from 'utils/user'
import OnwardSharingCheckbox from 'components/OnwardSharingCheckbox'
import SkeletonBar from 'components/SkeletonBar'

const api = APIService()

const FolderPermissionsIndicator = ({
  userCount = 0,
  folderId,
  folderType,
  customView,
  isOwner = false,
  onPermissionsUpdated = null,
  bp: bpProp = null
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [users, setUsers] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [domainUserIds, setDomainUserIds] = useState([])
  const [onwardSharing, setOnwardSharing] = useState(null)
  const [initialOnwardSharing, setInitialOnwardSharing] = useState(null)
  const selectedBp = useSelector(getSelectedBp)
  const bp = bpProp ?? selectedBp
  const accessMode = folderType === 'custom' ? customView?.accessType : null
  const modeConfig = {
    domain: { Icon: AlternateEmailRoundedIcon, label: 'Organisation', tone: 'domain', hint: 'Everyone in your organisation can open this, across all your organisation’s email domains, not just one. Adding named people below limits it to only those you choose.' },
    private: { Icon: LockOutlinedIcon, label: 'Private', tone: 'private', hint: 'Private: only Admins can open this folder.' }
  }
  const mode = accessMode ? modeConfig[accessMode] : null
  const renderModeChip = () => {
    if (!mode) return null
    const ModeIcon = mode.Icon
    return (
      <Tooltip title={mode.hint} placement="top" arrow>
        <ModeChip $tone={mode.tone}><ModeIcon style={{ fontSize: 13 }} /></ModeChip>
      </Tooltip>
    )
  }
  const renderModeHint = () => {
    if (!mode) return null
    const ModeIcon = mode.Icon
    return <ModeHint><ModeIcon style={{ fontSize: 15 }} /> <span>{mode.hint}</span></ModeHint>
  }

  const handleClick = e => {
    e.stopPropagation()
    if (isOwner) {
      setModalOpen(true)
      loadUsers()
    }
  }

  const loadUsers = () => {
    setLoading(true)
    api.get(`/building-passport/${bp.id}/assigned-users`)
      .then(resp => {
        const assignedUsers = resp?.data?.data || []
        setUsers(assignedUsers)
        loadCurrentPermissions(assignedUsers)
      })
      .catch(err => {
        console.error('Failed to load users:', err)
        setLoading(false)
      })
    if (accessMode === 'domain' && customView?.id) {
      api.get(`/building-passport/${bp.id}/custom-views/${customView.id}/domain-users`)
        .then(resp => setDomainUserIds(resp?.data?.data?.userIds || []))
        .catch(() => setDomainUserIds([]))
    }
  }

  const loadCurrentPermissions = (assignedUsers) => {
    api.get(`/building-passport/${bp.id}/folders/permissions`, {
      params: {
        folder_id: folderId,
        folder_type: folderType
      }
    })
      .then(resp => {
        const userIds = resp?.data?.data || []
        const onward = typeof resp?.data?.onwardSharingAllowed === 'boolean' ? resp.data.onwardSharingAllowed : null
        setOnwardSharing(onward)
        setInitialOnwardSharing(onward)
        const ownerIds = assignedUsers.filter(u => u.isOwner).map(u => u.id)
        const allSelectedIds = [...new Set([...userIds, ...ownerIds])]
        setSelectedUsers(allSelectedIds)
        setLoading(false)
      })
      .catch(err => {
        console.error('Failed to load permissions:', err)
        const ownerIds = assignedUsers.filter(u => u.isOwner).map(u => u.id)
        setSelectedUsers(ownerIds)
        setLoading(false)
      })
  }

  const handleToggleUser = (userId, locked) => {
    if (locked) return
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const handleSave = () => {
    setSaving(true)

    let endpoint = ''
    if (folderType === 'custom') {
      endpoint = `/building-passport/${bp.id}/custom-views/${customView?.id}/folders/access`
    } else if (folderType === 'folder') {
      endpoint = `/building-passport/${bp.id}/folders-access`
    } else if (folderType === 'category') {
      endpoint = `/building-passport/${bp.id}/categories-access`
    }

    const onwardChanged = onwardSharing !== null && onwardSharing !== initialOnwardSharing

    Promise.all([
      ...users.filter(user => !user.isOwner && !domainUserIds.includes(user.id)).map(user =>
        api.patch(endpoint, {
          userId: user.id,
          [folderType === 'category' ? 'categories' : 'folders']: [{
            id: folderId,
            permission: selectedUsers.includes(user.id)
          }]
        })
      ),
      ...(onwardChanged
        ? [api.patch(`/building-passport/${bp.id}/folders/onward-sharing`, {
            kind: folderType,
            targetId: folderId,
            allowed: onwardSharing
          })]
        : [])
    ])
      .then(() => {
        toast.success('Permissions updated')
        if (onwardChanged) {
          toast.success(`Onward sharing turned ${onwardSharing ? 'on' : 'off'} for this folder.`)
        }
        setModalOpen(false)
        setSaving(false)
        onPermissionsUpdated?.()
      })
      .catch(err => {
        toast.error(getApiErrors(err))
        setSaving(false)
      })
  }

  const formatRole = (relationType) => {
    if (!relationType) return 'User'
    return relationType.split(', ').map(buildingRoleLabel).join(', ')
  }

  const selectedCount = selectedUsers.length

  return (
    <>
      <PermissionsIconWrapper onClick={handleClick} isClickable={isOwner}>
        <Tooltip
          title={
            isOwner
              ? `${userCount} named ${userCount === 1 ? 'person has' : 'people have'} folder access. Click to manage.`
              : `${userCount} named ${userCount === 1 ? 'person has' : 'people have'} folder access`
          }
          placement="top"
          arrow
        >
          <StyledIconButton size="small" disabled={!isOwner}>
            <StyledPeopleIcon fontSize="small" />
            <CountBadge>{userCount}</CountBadge>
          </StyledIconButton>
        </Tooltip>
        {renderModeChip()}
      </PermissionsIconWrapper>

      <StyledDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="xs"
        fullWidth
        onClick={e => e.stopPropagation()}
      >
        <Header>
          <HeaderContent>
            <HeaderIcon>
              <PeopleIcon style={{ fontSize: 18 }} />
            </HeaderIcon>
            <HeaderText>
              <Title>Folder Access</Title>
              {!loading && (
                <Subtitle>
                  {accessMode === 'domain'
                    ? 'Everyone in your organisation has access'
                    : accessMode === 'private'
                      ? 'Private: Admins only'
                      : `${selectedCount} user${selectedCount !== 1 ? 's' : ''} with access`}
                </Subtitle>
              )}
            </HeaderText>
          </HeaderContent>
          <CloseBtn onClick={() => setModalOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </CloseBtn>
        </Header>

        <Body>
          {renderModeHint()}
          {!loading && onwardSharing !== null && (
            <OnwardBox data-tour-id='folder-access.onward-sharing'>
              <OnwardSharingCheckbox
                checked={onwardSharing}
                onChange={setOnwardSharing}
                label='Onward sharing allowed for documents in this folder'
                helper='Unticked means only each document’s uploader decides requests.'
              />
            </OnwardBox>
          )}
          {loading ? (
            <SkeletonList aria-hidden='true'>
              {[0, 1, 2].map(i => (
                <SkeletonRow key={i}>
                  <SkeletonBar $round $width='36px' $height='36px' />
                  <SkeletonLines>
                    <SkeletonBar $width='45%' $height='12px' />
                    <SkeletonBar $width='70%' $height='12px' />
                  </SkeletonLines>
                </SkeletonRow>
              ))}
            </SkeletonList>
          ) : (
            <UserList>
              {users.map(user => {
                const isOwnerUser = user.isOwner
                const isDomainUser = domainUserIds.includes(user.id)
                const locked = isOwnerUser || isDomainUser
                const hasAccess = selectedUsers.includes(user.id) || locked

                return (
                  <UserRow
                    key={user.id}
                    onClick={() => handleToggleUser(user.id, locked)}
                    disabled={locked}
                    selected={hasAccess}
                  >
                    <UserAvatar firstName={user.firstName || user.name} lastName={user.lastName} email={user.email || user.name} src={user.avatar} size={36} />
                    <UserInfo>
                      <UserNameRow>
                        <UserName>{user.name || user.email}</UserName>
                        {isOwnerUser && <OwnerTag>{buildingRoleLabel('owner')}</OwnerTag>}
                        {!isOwnerUser && isDomainUser && (
                          <Tooltip title="Has access via your organisation, so it can’t be changed here" placement="top" arrow>
                            <OrgTag>Via organisation</OrgTag>
                          </Tooltip>
                        )}
                      </UserNameRow>
                      <UserMeta>
                        {user.name && <UserEmail>{hideEmail(user.email || null)}</UserEmail>}
                        <RoleDot />
                        <RoleText>{formatRole(user.relationType)}</RoleText>
                      </UserMeta>
                    </UserInfo>
                    <Checkbox checked={hasAccess} disabled={locked}>
                      <CheckIcon style={{ fontSize: 14 }} />
                    </Checkbox>
                  </UserRow>
                )
              })}
            </UserList>
          )}
        </Body>

        <Footer>
          <FooterHint>{buildingRoleLabel('owner')}s always have full access</FooterHint>
          <FooterActions>
            <CancelBtn onClick={() => setModalOpen(false)}>Cancel</CancelBtn>
            <SaveBtn onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving...' : 'Save Changes'}
            </SaveBtn>
          </FooterActions>
        </Footer>
      </StyledDialog>
    </>
  )
}

export default FolderPermissionsIndicator

const PermissionsIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
`

const StyledIconButton = styled(IconButton)`
  padding: 4px !important;
  display: flex !important;
  align-items: center !important;
  gap: 2px !important;
`

const StyledPeopleIcon = styled(PeopleIcon)`
  color: #666;
  font-size: 18px !important;
`

const CountBadge = styled.span`
  color: #666;
  font-size: 9px;
  margin-left: 1px;
`

const MODE_TONES = {
  domain: { bg: '#eaf1fe', fg: '#3b5ba5' },
  private: { bg: '#eef2f7', fg: '#475569' },
  public: { bg: '#e6f7f9', fg: '#0f6f78' }
}

const ModeChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $tone }) => (MODE_TONES[$tone] || MODE_TONES.private).bg};
  color: ${({ $tone }) => (MODE_TONES[$tone] || MODE_TONES.private).fg};
`

const ModeHint = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 20px 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #64748b;

  svg { color: #94a3b8; flex-shrink: 0; }
`

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #33becc 0%, #2da8b5 100%);
  color: white;
`

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HeaderIcon = styled.div`
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const HeaderText = styled.div``

const Title = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`

const Subtitle = styled.div`
  font-size: 12px;
  opacity: 0.9;
  margin-top: 2px;
`

const CloseBtn = styled(IconButton)`
  padding: 6px !important;
  color: white !important;
  opacity: 0.8;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1) !important;
  }
`

const Body = styled.div`
  max-height: 340px;
  overflow-y: auto;
`

const OnwardBox = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid #e5e7eb;
`

const SkeletonList = styled.div`
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SkeletonLines = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const UserList = styled.div`
  padding: 8px 0;
`

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  background: ${props => props.selected ? 'rgba(51, 190, 204, 0.08)' : 'transparent'};
  border-left: 3px solid ${props => props.selected ? '#33becc' : 'transparent'};
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.disabled
      ? (props.selected ? 'rgba(51, 190, 204, 0.08)' : 'transparent')
      : (props.selected ? 'rgba(51, 190, 204, 0.12)' : 'rgba(0, 0, 0, 0.03)')
    };
  }
`

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const UserNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const UserName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const OwnerTag = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(51, 190, 204, 0.15);
  color: #2da8b5;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const OrgTag = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: #eaf1fe;
  color: #3b5ba5;
  letter-spacing: 0.3px;
`

const UserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const UserEmail = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RoleDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.grey[700]};
`

const RoleText = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid ${props => props.checked ? '#33becc' : '#ddd'};
  background: ${props => props.checked ? '#33becc' : '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.15s ease;

  svg {
    opacity: ${props => props.checked ? 1 : 0};
    transform: scale(${props => props.checked ? 1 : 0.5});
    transition: all 0.15s ease;
  }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-top: 1px solid ${({ theme }) => theme.colors.grey[500]};
`

const FooterHint = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const FooterActions = styled.div`
  display: flex;
  gap: 10px;
`

const CancelBtn = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fff;
    border-color: ${({ theme }) => theme.colors.grey[700]};
  }
`

const SaveBtn = styled.button`
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #33becc 0%, #2da8b5 100%);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 2px 8px rgba(51, 190, 204, 0.3);

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(51, 190, 204, 0.4);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`
